import { EventEmitter, inject,Injectable } from '@angular/core';
import { Filter, Order, OrderType, Paginator, Search } from '@docpath/linces-angular-components/common';
import { Table, TableField, TableFieldBadge, TableFieldBadges, TableFieldIcon, TableFieldIcons, TableFieldIconsText, TableFieldLink, TableFieldText, TableRow } from '@docpath/webcomponents';
import { faComment, faEnvelope } from '@fortawesome/free-regular-svg-icons';
import { Constants } from 'src/app/constants/constants';

import { Alias } from '../../../alias/models/alias';
import { AlertRule, Priority, Via } from '../../models/alert-rule';
import { AlertRulesFormService } from '../../services/alert-rules-form.service';
import { AlertRulesHttpService } from '../../services/alert-rules-http.service';

@Injectable({
    providedIn: 'root'
})
export class AlertRulesTableService {

    private readonly i18nPrefix = 'alert-rules-config.table';

    alertRulesLoaded: EventEmitter<AlertRule[]> = new EventEmitter();

    private currentPage: number;
    private elements: number;
    private currentSize: number;
    private alertRules: AlertRule[] = [];

    private readonly alertRulesHttpService = inject(AlertRulesHttpService);
    private readonly alertRulesFormService = inject(AlertRulesFormService);

    constructor() {
        this.currentPage = 1;
        this.elements = 0;
        this.currentSize = 25;
    }

    initTable(): Table {

        const header = this.initTableHeader();
        const body = this.initTableBody([], []);

        return new Table(header, body);

    }

    initTableHeader(): TableRow {

        return new TableRow(
            [
                new TableFieldText(`${this.i18nPrefix}.rule-name`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefix}.rule_description`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefix}.alerts`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefix}.priority.label`, undefined, undefined, undefined, undefined, true)
            ]
        );

    }

    initTableBody(alertRules: AlertRule[], alias: Alias[]): TableRow[] {

        return alertRules.map(alertRule => {
            const fields: TableField[] = [], nameClickAction = () => this.alertRulesFormService.openEditAlertRuleForm(alertRule, alias);
            fields.push(new TableFieldIconsText(
                new TableFieldIcons([new TableFieldIcon(['fas', 'square'], undefined, undefined, undefined, undefined, { style: `color:${alertRule.color} !important` })]),
                new TableFieldLink(alertRule.rule_name, alertRule.rule_name, undefined, nameClickAction, alertRule.rule_name)));
            fields.push(new TableFieldText(alertRule.rule_description || '', undefined, undefined));
            fields.push(this.getAlertRulesField(alertRule));
            fields.push(new TableFieldBadges([
                new TableFieldBadge(`${this.i18nPrefix}.priority.option.${alertRule.priority}`, undefined, { cssClass: this.getPriority(alertRule.priority) }, true)
            ]));

            const tableRow = new TableRow(fields);
            tableRow.content = alertRule;

            return tableRow;
        });

    }

    private getAlertRulesField(alertRule: AlertRule): TableFieldIcons {
        const alertsRulesFieldData: Array<TableFieldIcon> = [];

        alertRule.rule_alias.forEach(rule_alias => {
            if (rule_alias.via === Via.Email) {
                const emailIcon = new TableFieldIcon(faEnvelope, `${rule_alias.alias.alert_alias} - ${rule_alias.alias.email}`, undefined, undefined, undefined, { cssClass: 'ms-2 text-dpmedium' });
                alertsRulesFieldData.push(emailIcon);
            } else if (rule_alias.via === Via.SMS) {
                const smsIcon = new TableFieldIcon(faComment, `${rule_alias.alias.alert_alias} - ${rule_alias.alias.phone_number}`, undefined, undefined, undefined, { cssClass: 'ms-2 text-dpmedium' });
                alertsRulesFieldData.push(smsIcon);
            }
        });

        return new TableFieldIcons(alertsRulesFieldData);
    }

    private getPriority(priority: Priority): string {
        const cssClass: { [key: string]: string; } = {
            critical: 'bg-danger',
            medium: 'bg-warning',
            low: 'bg-success'
        };

        return cssClass[priority] || '';
    }

    loadAlertRules(filters: Filter[], page = this.currentPage, size = this.currentSize): void {

        this.currentPage = page;
        this.currentSize = size;

        const search = new Search(filters, [new Order(Constants.CONF_ALERT_RULES_RULE_NAME, OrderType.Asc)], [], new Paginator(page - 1, this.currentSize, this.elements));

        this.alertRulesHttpService.getAlertRules(search, (result, totalSize) => {
            this.elements = totalSize;
            this.alertRules = result as AlertRule[];
            this.alertRulesLoaded.emit(result);
        });

    }

    getCurrentPage(): number {
        return this.currentPage;
    }

    getElements(): number {
        return this.elements;
    }

    getAlertRules(): AlertRule[] {
        return this.alertRules;
    }

    getSelectedAlertRule(table: Table): AlertRule[] {
        return table.body.filter(row => row.selected).map(row => row.content);
    }

    getCurrentSize(): number {
        return this.currentSize;
    }

}